import { ChevronLeft, Download, Plus, Upload } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { ClaimDocuments } from './ClaimDocuments'
import {
  ADJUSTER,
  CARRIER_NAME,
  type ClaimOutcome,
  ClaimStatus,
  INSURED_NAME,
  STATUS_LABELS,
} from './claimsData'
import { ClaimTimeline } from './ClaimTimeline'
import { timelineForStatus } from './timelineSteps'

const SUMMARY = [
  { label: 'Insured', value: INSURED_NAME },
  { label: 'Vehicle', value: '2015 Kenworth T680' },
  { label: 'Incident date', value: 'Aug 29, 2026' },
]

interface ClaimTrackerProps {
  status: ClaimStatus
  outcome?: ClaimOutcome
  needsYou?: boolean
}

/** One claim, end to end. The zones say who is holding it at each point. */
export function ClaimTracker({ status, outcome, needsYou }: ClaimTrackerProps) {
  const withCai = status !== ClaimStatus.OPEN_WITH_CARRIER && status !== ClaimStatus.CLOSED
  const closed = status === ClaimStatus.CLOSED

  return (
    <div className="max-w-2xl">
      <Button variant="link" size="sm" className="mb-3 px-0 text-xs font-semibold">
        <ChevronLeft />
        All claims
      </Button>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <div className="text-ink-400 font-mono text-xs uppercase">Claim ZUS-A9T</div>
          <h1 className="mt-1 text-2xl font-bold tracking-[-0.01em] text-balance">
            Rear-end collision, Grand Island NE
          </h1>
        </div>
        <Badge variant="outline" className="shrink-0 self-start">
          {STATUS_LABELS[status]}
        </Badge>
      </div>

      <dl className="border-ink-200 bg-surface mt-5 flex flex-wrap gap-5 rounded-sm border px-4 py-3.5">
        {SUMMARY.map((item) => (
          <div key={item.label} className="min-w-32 flex-1">
            <dt className="text-ink-400 font-mono text-[10px] tracking-[0.12em] uppercase">{item.label}</dt>
            <dd className="mt-0.5 text-sm font-semibold">{item.value}</dd>
          </div>
        ))}
      </dl>

      {!closed && (
        <>
          <div className="mt-4.5 flex flex-wrap items-center gap-2">
            <Button variant="outlineThin" size="sm" disabled={!withCai}>
              <Plus />
              Add information
            </Button>
            <Button variant="outlineThin" size="sm">
              <Upload />
              Upload documents
            </Button>
            <Button variant="ghost" size="sm" className="ml-auto">
              <Download />
              Download claim package
            </Button>
          </div>
          <p className="text-ink-400 mt-1.5 text-xs">
            {withCai
              ? 'You can still change anything on this claim while it is with Cai.'
              : `${CARRIER_NAME} has the claim now. Anything you add here, we pass to ${ADJUSTER.name}.`}
          </p>
        </>
      )}

      <div className="mt-7">
        <ClaimTimeline steps={timelineForStatus(status, outcome, needsYou)} outcome={outcome} />
      </div>

      <ClaimDocuments />

      <Banner variant="info" small className="mt-10">
        {CARRIER_NAME} tells us only whether a claim is open or closed. Anything more detailed came from your
        broker speaking to the adjuster, so it updates when we hear something, not on a schedule.
      </Banner>
    </div>
  )
}
